import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faCheckCircle, 
    faUserShield, 
    faHandshake, 
    faChartLine,
    faClock,
    faLock,
    faPhone,
    faEnvelope,
    faMapMarkerAlt,
    faCar,
    faHome,
    faGem
} from '@fortawesome/free-solid-svg-icons';
import './Home.css';
import porscheHeroImage from '../../images/porsche-hero-image.jpg';

// Sample lead data for preview
const sampleLeads = [
    {
        id: 1,
        type: 'Car',
        name: 'John F',
        lookingFor: '1996 Aston Martin V8 Vantage',
        mileage: 'Under 100,000',
        location: 'Indianapolis, IN',
        distance: 'Any (Willing to Travel or Ship)',
        phone: 'Available (Verified)',
        email: 'Available (Unverified)',
        readiness: 'Ready to Buy Now'
    },
    {
        id: 2,
        type: 'Car',
        name: 'Sarah M',
        lookingFor: '2018-2021 Porsche 911 GT3',
        mileage: 'Under 30,000',
        location: 'Chicago, IL',
        distance: 'Within 300 miles',
        phone: 'Available (Verified)',
        email: 'Available (Verified)',
        readiness: 'Actively Searching'
    },
    {
        id: 3,
        type: 'Real Estate',
        name: 'Michael T',
        lookingFor: 'Luxury Condo in Downtown Area',
        details: '2+ bedrooms, 2+ bathrooms',
        location: 'Miami, FL',
        distance: 'Miami Metro Area Only',
        phone: 'Available (Verified)',
        email: 'Available (Verified)',
        readiness: 'Pre-Approved for Financing'
    },
    {
        id: 4,
        type: 'Jewelry',
        name: 'Elizabeth K',
        lookingFor: 'Vintage Diamond Engagement Ring',
        details: '1-2 carat, Art Deco style',
        location: 'New York, NY',
        distance: 'Nationwide',
        phone: 'Available upon request',
        email: 'Available (Verified)',
        readiness: 'Ready to Purchase'
    },
    {
        id: 5,
        type: 'Car',
        name: 'Robert J',
        lookingFor: 'Ferrari 458 Italia',
        mileage: 'Under 20,000',
        location: 'Los Angeles, CA',
        distance: 'West Coast Only',
        phone: 'Available (Verified)',
        email: 'Available (Verified)',
        readiness: 'Funds Ready'
    }
];

// Function to get the appropriate icon for lead type
const getLeadTypeIcon = (type) => {
    switch(type.toLowerCase()) {
        case 'car':
            return faCar;
        case 'real estate':
            return faHome;
        case 'jewelry':
            return faGem;
        default:
            return faCheckCircle;
    }
};

const SellersHome = () => {
    return (
        <main className="sellers-home">
            <section className="sellers-hero">
                <div className="hero-overlay">
                    <div className="container">
                        <div className="hero-content">
                            <h1 className="hero-title">Find Verified Buyers Looking For Exactly What You Are Selling.</h1>
                            <p className="hero-subtitle">
                                Join our exclusive network of verified sellers and connect with serious buyers looking for the items that are exactly what you have to sell them.
                            </p>
                            <Link to="/get-started" className="cta-button">
                                    Browse Verified Leads Now
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Leads Preview Section */}
            <section className="leads-preview-section">
                <div className="container">
                    <div className="leads-preview-header">
                        <h2>Recent Buyer Requests</h2>
                        <p>Here's a preview of recent requests from verified buyers. Sign up to access all leads and filtering options.</p>
                    </div>
                    
                    <div className="leads-preview-grid">
                        {sampleLeads.map(lead => (
                            <div className="lead-card" key={lead.id}>
                                <div className="lead-card-header">
                                    <div className="lead-type">
                                        <FontAwesomeIcon icon={getLeadTypeIcon(lead.type)} />
                                        <span>{lead.type}</span>
                                    </div>
                                    <div className="lead-readiness">{lead.readiness}</div>
                                </div>
                                
                                <div className="lead-card-body">
                                    <h3>{lead.name} - Looking for:</h3>
                                    <p className="lead-item">{lead.lookingFor}</p>
                                    
                                    {lead.mileage && (
                                        <div className="lead-detail">
                                            <strong>Ideal Mileage:</strong> {lead.mileage}
                                        </div>
                                    )}
                                    
                                    {lead.details && (
                                        <div className="lead-detail">
                                            <strong>Details:</strong> {lead.details}
                                        </div>
                                    )}
                                    
                                    <div className="lead-detail">
                                        <FontAwesomeIcon icon={faMapMarkerAlt} className="lead-icon" />
                                        <strong>Located In:</strong> {lead.location}
                                    </div>
                                    
                                    <div className="lead-detail">
                                        <strong>Preferred Distance:</strong> {lead.distance}
                                    </div>
                                    
                                    <div className="lead-contact-info">
                                        <div className="contact-item">
                                            <FontAwesomeIcon icon={faPhone} className="lead-icon" />
                                            <span>Phone: {lead.phone}</span>
                                        </div>
                                        <div className="contact-item">
                                            <FontAwesomeIcon icon={faEnvelope} className="lead-icon" />
                                            <span>Email: {lead.email}</span>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="lead-card-footer">
                                    <Link to="/get-started" className="view-lead-button">
                                        Access Full Details
                                    </Link>
                                </div>
                            </div>
                        ))}
                    </div>
                    
                    <div className="leads-preview-cta">
                        <p>Want to see more leads like these?</p>
                        <Link to="/get-started" className="secondary-cta">
                            Sign Up for Full Access
                        </Link>
                    </div>
                </div>
            </section>

            <section className="benefits-section">
                <div className="container">
                    <h2>Why Sell with Us?</h2>
                    <div className="benefits-grid">
                        <div className="benefit-card">
                            <FontAwesomeIcon icon={faUserShield} className="benefit-icon" />
                            <h3>Verified Buyers Only</h3>
                            <p>Connect with pre-qualified buyers who are serious about purchasing premium items.</p>
                        </div>
                        <div className="benefit-card">
                            <FontAwesomeIcon icon={faHandshake} className="benefit-icon" />
                            <h3>Secure Transactions</h3>
                            <p>Our secure platform handles payments and protects both parties throughout the process.</p>
                        </div>
                        <div className="benefit-card">
                            <FontAwesomeIcon icon={faChartLine} className="benefit-icon" />
                            <h3>Maximum Exposure</h3>
                            <p>Get your items in front of the right audience with our targeted matching system.</p>
                        </div>
                    </div>
                </div>
            </section>

            <section className="how-it-works">
                <div className="container">
                    <h2>How It Works</h2>
                    <div className="steps-grid">
                        <div className="step-card">
                            <div className="step-number">1</div>
                            <h3>Create Your Account</h3>
                            <p>Sign up and complete your seller verification process.</p>
                        </div>
                        <div className="step-card">
                            <div className="step-number">2</div>
                            <h3>List Your Items</h3>
                            <p>Add your premium items with detailed descriptions and photos.</p>
                        </div>
                        <div className="step-card">
                            <div className="step-number">3</div>
                            <h3>Connect with Buyers</h3>
                            <p>Get matched with verified buyers interested in your items.</p>
                        </div>
                    </div>
                </div>
            </section>

            <section className="features-section">
                <div className="container">
                    <div className="features-grid">
                        <div className="feature-content">
                            <h2>Premium Seller Features</h2>
                            <ul className="feature-list">
                                <li>
                                    <FontAwesomeIcon icon={faCheckCircle} className="check-icon" />
                                    Dedicated account manager
                                </li>
                                <li>
                                    <FontAwesomeIcon icon={faCheckCircle} className="check-icon" />
                                    Priority listing placement
                                </li>
                                <li>
                                    <FontAwesomeIcon icon={faCheckCircle} className="check-icon" />
                                    Advanced analytics and insights
                                </li>
                                <li>
                                    <FontAwesomeIcon icon={faCheckCircle} className="check-icon" />
                                    Professional photography services
                                </li>
                            </ul>
                            <Link to="/get-started" className="secondary-cta">
                                Register Now
                            </Link>
                        </div>
                        <div className="trust-indicators">
                            <div className="trust-item">
                                <FontAwesomeIcon icon={faClock} className="trust-icon" />
                                <h4>Quick Setup</h4>
                                <p>Be ready to sell in minutes</p>
                            </div>
                            <div className="trust-item">
                                <FontAwesomeIcon icon={faLock} className="trust-icon" />
                                <h4>Secure Platform</h4>
                                <p>Bank-level security</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
};

export default SellersHome;
